import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, catchError, map, of } from 'rxjs';
import { KNOWN_ROUTE_SEGMENTS } from '../../app-routing.module';
import { BaseConnectionService } from './base-connection.service';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class GroupLinkService extends BaseConnectionService {
  private groupLink: string | null = null;
  private http = inject(HttpClient);
  private configurationService = inject(ConfigurationService);

  constructor() {
    super();
    this.loadFromSessionStorage();
  }

  private getSessionStorageKey(): string {
    const client = this.configurationService.environment || 'default';
    return `gs-group-link-${client}`;
  }

  extractGroupLinkFromUrl(url: string): string | null {
    const segments = url.split('/').filter(segment => segment.length > 0);

    if (segments.length === 0) {
      return null;
    }

    const firstSegment = segments[0];

    const isKnownRoute = KNOWN_ROUTE_SEGMENTS.includes(firstSegment);

    return isKnownRoute ? null : firstSegment;
  }

  validateGroupLink(groupLink: string): Observable<boolean> {
    const endpoint = `${environment.apiUrl}/group-links/${groupLink}`;
    return this.http.get(endpoint).pipe(
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        return of(false);
      })
    );
  }

  setGroupLink(groupLink: string): void {
    this.groupLink = groupLink;
    sessionStorage.setItem(this.getSessionStorageKey(), groupLink);
  }

  clearGroupLink(): void {
    this.groupLink = null;
    sessionStorage.removeItem(this.getSessionStorageKey());
  }

  private loadFromSessionStorage(): void {
    const storedGroupLink = sessionStorage.getItem(this.getSessionStorageKey());
    if (storedGroupLink) {
      this.groupLink = storedGroupLink;
    }
  }

  prependGroupLink(segments: (string | number)[]): (string | number)[] {
    if (this.groupLink) {
      const updatedSegments = [...segments];
      let isAbsolute = false;

      if (
        updatedSegments.length > 0 &&
        typeof updatedSegments[0] === 'string' &&
        (updatedSegments[0].startsWith('/') || updatedSegments[0] === '')
      ) {
        isAbsolute = true;
        if (updatedSegments[0].startsWith('/')) {
          updatedSegments[0] = updatedSegments[0].substring(1);
        }
        if (updatedSegments[0] === '') {
          updatedSegments.shift();
        }
      }

      if (
        updatedSegments.length > 0 &&
        typeof updatedSegments[0] === 'string' &&
        updatedSegments[0].startsWith(this.groupLink)
      ) {
        return isAbsolute ? ['/', ...updatedSegments] : [...updatedSegments];
      }

      return isAbsolute
        ? ['/', this.groupLink, ...updatedSegments]
        : [this.groupLink, ...updatedSegments];
    }

    return segments;
  }

  getGroupLink(): string | null {
    return this.groupLink;
  }
}
